"use client";

import { c as _c } from "react/compiler-runtime";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useAndroidKeyboard } from "@/hooks/useAndroidKeyboard";
import useCapacitor from "@/hooks/useCapacitor";
import type { ReactNode } from "react";

// Define the different spaces that need to be measured
interface LayoutSpaces {
  // Space used by persistent UI elements (header, breadcrumb, etc)
  headerSpace: number;

  // Space used by dynamic elements (pinned waves, etc)
  pinnedSpace: number;

  // Space used by tab elements when present
  tabsSpace: number;

  // Space used by spacer elements
  spacerSpace: number;

  // Space used by mobile tabs
  mobileTabsSpace: number;

  // Space used by mobile nav
  mobileNavSpace: number;
  // Available space for content
  contentSpace: number;

  // Whether measurements have completed
  measurementsComplete: boolean;
}
const spacesAreEqual = (a: LayoutSpaces, b: LayoutSpaces) => a.headerSpace === b.headerSpace && a.pinnedSpace === b.pinnedSpace && a.tabsSpace === b.tabsSpace && a.spacerSpace === b.spacerSpace && a.mobileTabsSpace === b.mobileTabsSpace && a.mobileNavSpace === b.mobileNavSpace && a.contentSpace === b.contentSpace && a.measurementsComplete === b.measurementsComplete;

// Helper function to calculate height style
const calculateHeightStyle = (spaces: LayoutSpaces, capacitorSpace: number): React.CSSProperties => {
  // Use dynamic viewport height to avoid extra space on mobile browsers
  const heightCalc = `calc(100dvh - ${spaces.headerSpace}px - ${spaces.pinnedSpace}px - ${spaces.tabsSpace}px - ${spaces.spacerSpace}px - ${spaces.mobileTabsSpace}px - ${spaces.mobileNavSpace}px - ${capacitorSpace}px)`;
  return {
    height: heightCalc,
    maxHeight: heightCalc
  };
};

// Context type definition
// Define valid ref types for type safety
type LayoutRefType = "header" | "pinned" | "tabs" | "spacer" | "mobileTabs" | "mobileNav";
interface LayoutContextType {
  // Calculated spaces
  spaces: LayoutSpaces;

  // Registration system
  registerRef: (refType: LayoutRefType, element: HTMLDivElement | null) => void;

  // Pre-calculated styles for main containers
  contentContainerStyle: React.CSSProperties;

  // Style for chat view
  waveViewStyle: React.CSSProperties;

  // Style for leaderboard view
  leaderboardViewStyle: React.CSSProperties;

  // Style for winners view
  winnersViewStyle: React.CSSProperties;

  // Style for sales view
  salesViewStyle: React.CSSProperties;

  // Style for outcome view
  outcomeViewStyle: React.CSSProperties;

  // Style for my votes view
  myVotesViewStyle: React.CSSProperties;

  // Style for FAQ view
  faqViewStyle: React.CSSProperties;

  // Style for notifications view
  notificationsViewStyle: React.CSSProperties;

  // Style for feed view
  myStreamFeedStyle: React.CSSProperties;

  // Style for homepage view (excludes header/breadcrumbs, includes tabs)
  homepageFeedStyle: React.CSSProperties;

  // Style for small screen layout (includes header and tabs)
  smallScreenFeedStyle: React.CSSProperties;

  // Style for mobile waves view
  mobileWavesViewStyle: React.CSSProperties;

  // Style for mobile about view
  mobileAboutViewStyle: React.CSSProperties;
}

// Default context values
const defaultSpaces: LayoutSpaces = {
  headerSpace: 0,
  pinnedSpace: 0,
  tabsSpace: 0,
  spacerSpace: 0,
  mobileTabsSpace: 0,
  mobileNavSpace: 0,
  contentSpace: 0,
  measurementsComplete: false
};

// Create context
const LayoutContext = createContext<LayoutContextType>({
  spaces: defaultSpaces,
  registerRef: () => {},
  // No-op for default value
  contentContainerStyle: {},
  // Empty style object as default
  waveViewStyle: {},
  // Empty style object as default
  leaderboardViewStyle: {},
  // Empty style object as default
  winnersViewStyle: {},
  // Empty style object as default
  salesViewStyle: {},
  // Empty style object as default
  myVotesViewStyle: {},
  // Empty style object as default
  outcomeViewStyle: {},
  // Empty style object as default
  faqViewStyle: {},
  // Empty style object as default for FAQ
  notificationsViewStyle: {},
  // Empty style object as default
  myStreamFeedStyle: {},
  // Empty style object as default
  homepageFeedStyle: {},
  // Empty style object as default
  smallScreenFeedStyle: {},
  // Empty style object as default
  mobileWavesViewStyle: {},
  // Empty style object as default
  mobileAboutViewStyle: {} // Empty style object as default
});

// Provider component
export const LayoutProvider: React.FC<{
  children: ReactNode;
}> = t0 => {
  const $ = _c(75);
  const {
    children
  } = t0;
  const {
    isAndroid,
    isIos,
    keyboardVisible: isIosKeyboardVisible
  } = useCapacitor();
  const {
    isVisible: isAndroidKeyboardVisible
  } = useAndroidKeyboard();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      header: null,
      pinned: null,
      tabs: null,
      spacer: null,
      mobileTabs: null,
      mobileNav: null
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const refMap = useRef(t1);
  const resizeObserverRef = useRef(null);
  const [spaces, setSpaces] = useState(defaultSpaces);
  const calculateSpacesRef = useRef(_temp);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = (refType, element) => {
      if (refMap.current[refType] === element) {
        return;
      }
      const previousElement = refMap.current[refType];
      refMap.current[refType] = element;
      if (resizeObserverRef.current) {
        if (previousElement) {
          resizeObserverRef.current.unobserve(previousElement);
        }
        if (element) {
          resizeObserverRef.current.observe(element);
        }
      }
      requestAnimationFrame(() => calculateSpacesRef.current());
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const registerRef = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      let headerHeight = 0;
      let pinnedHeight = 0;
      let tabsHeight = 0;
      let spacerHeight = 0;
      let mobileTabsHeight = 0;
      let mobileNavHeight = 0;
      const headerElement = refMap.current.header;
      const pinnedElement = refMap.current.pinned;
      const tabsElement = refMap.current.tabs;
      const spacerElement = refMap.current.spacer;
      const mobileTabsElement = refMap.current.mobileTabs;
      const mobileNavElement = refMap.current.mobileNav;
      if (headerElement) {
        ;
        try {
          const rect = headerElement.getBoundingClientRect();
          headerHeight = rect.height;
        } catch (t4) {
          const e = t4;
          console.error("Error measuring header element:", e);
        }
      }
      if (pinnedElement) {
        ;
        try {
          const rect_0 = pinnedElement.getBoundingClientRect();
          pinnedHeight = rect_0.height;
        } catch (t5) {
          const e_0 = t5;
          console.error("Error measuring pinned element:", e_0);
        }
      }
      if (tabsElement) {
        ;
        try {
          const rect_1 = tabsElement.getBoundingClientRect();
          tabsHeight = rect_1.height;
        } catch (t6) {
          const e_1 = t6;
          console.error("Error measuring tabs element:", e_1);
        }
      }
      if (spacerElement) {
        ;
        try {
          const rect_2 = spacerElement.getBoundingClientRect();
          spacerHeight = rect_2.height;
        } catch (t7) {
          const e_2 = t7;
          console.error("Error measuring spacer element:", e_2);
        }
      }
      if (mobileTabsElement) {
        ;
        try {
          const rect_3 = mobileTabsElement.getBoundingClientRect();
          mobileTabsHeight = rect_3.height;
        } catch (t8) {
          const e_3 = t8;
          console.error("Error measuring mobile tabs element:", e_3);
        }
      }
      if (mobileNavElement) {
        ;
        try {
          const rect_4 = mobileNavElement.getBoundingClientRect();
          mobileNavHeight = rect_4.height;
        } catch (t9) {
          const e_4 = t9;
          console.error("Error measuring mobile nav element:", e_4);
        }
      }
      const totalOccupiedSpace = headerHeight + pinnedHeight + tabsHeight + spacerHeight + mobileTabsHeight + mobileNavHeight;
      const calculatedContentSpace = Math.max(0, window.innerHeight - totalOccupiedSpace);
      const nextSpaces = {
        headerSpace: headerHeight,
        pinnedSpace: pinnedHeight,
        tabsSpace: tabsHeight,
        spacerSpace: spacerHeight,
        mobileTabsSpace: mobileTabsHeight,
        mobileNavSpace: mobileNavHeight,
        contentSpace: calculatedContentSpace,
        measurementsComplete: true
      };
      setSpaces(prevSpaces => spacesAreEqual(prevSpaces, nextSpaces) ? prevSpaces : nextSpaces);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const calculateSpaces = t3;
  let t4;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      resizeObserverRef.current = new ResizeObserver(() => {
        requestAnimationFrame(calculateSpaces);
      });
      Object.entries(refMap.current).forEach(t6 => {
        const [, element_0] = t6;
        if (element_0) {
          resizeObserverRef.current?.observe(element_0);
        }
      });
      window.addEventListener("resize", calculateSpaces);
      calculateSpaces();
      return () => {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }
        window.removeEventListener("resize", calculateSpaces);
      };
    };
    t5 = [calculateSpaces];
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      calculateSpacesRef.current = calculateSpaces;
    };
    t7 = [calculateSpaces];
    $[5] = t6;
    $[6] = t7;
  } else {
    t6 = $[5];
    t7 = $[6];
  }
  useEffect(t6, t7);
  let t8;
  bb0: {
    if (!spaces.measurementsComplete) {
      let t9;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = {};
        $[7] = t9;
      } else {
        t9 = $[7];
      }
      t8 = t9;
      break bb0;
    }
    const t9 = `calc(100dvh - ${spaces.headerSpace}px - ${spaces.spacerSpace}px)`;
    let t10;
    if ($[8] !== t9) {
      t10 = {
        height: t9,
        display: "flex"
      };
      $[8] = t9;
      $[9] = t10;
    } else {
      t10 = $[9];
    }
    t8 = t10;
  }
  const contentContainerStyle = t8;
  const isNavHiddenForKeyboard = isAndroid && isAndroidKeyboardVisible || isIos && isIosKeyboardVisible;
  let t9;
  if ($[10] !== isNavHiddenForKeyboard || $[11] !== spaces) {
    t9 = isNavHiddenForKeyboard ? {
      ...spaces,
      mobileNavSpace: 0
    } : spaces;
    $[10] = isNavHiddenForKeyboard;
    $[11] = spaces;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const navAdjustedSpaces = t9;
  let t10;
  bb1: {
    if (!navAdjustedSpaces.measurementsComplete) {
      let t11;
      if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t11 = {};
        $[13] = t11;
      } else {
        t11 = $[13];
      }
      t10 = t11;
      break bb1;
    }
    let capSpace = 0;
    let navSpace = navAdjustedSpaces.mobileNavSpace;
    if (isAndroid) {
      navSpace = isAndroidKeyboardVisible ? 0 : navAdjustedSpaces.mobileNavSpace;
      capSpace = isAndroidKeyboardVisible ? 0 : Math.max(0, 128 - navSpace);
    }
    let t11;
    if ($[14] !== capSpace || $[15] !== navAdjustedSpaces || $[16] !== navSpace) {
      t11 = calculateHeightStyle({
        ...navAdjustedSpaces,
        mobileNavSpace: navSpace
      }, capSpace);
      $[14] = capSpace;
      $[15] = navAdjustedSpaces;
      $[16] = navSpace;
      $[17] = t11;
    } else {
      t11 = $[17];
    }
    const style = t11;
    if (isAndroid) {
      let t12;
      if ($[18] !== style) {
        t12 = {
          ...style,
          transition: "height 75ms ease-out, max-height 75ms ease-out"
        };
        $[18] = style;
        $[19] = t12;
      } else {
        t12 = $[19];
      }
      t10 = t12;
      break bb1;
    }
    t10 = style;
  }
  const waveViewStyle = t10;
  let t11;
  bb2: {
    if (!navAdjustedSpaces.measurementsComplete) {
      let t12;
      if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
        t12 = {};
        $[20] = t12;
      } else {
        t12 = $[20];
      }
      t11 = t12;
      break bb2;
    }
    let t12;
    if ($[21] !== navAdjustedSpaces) {
      t12 = calculateHeightStyle(navAdjustedSpaces, 0);
      $[21] = navAdjustedSpaces;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    t11 = t12;
  }
  const leaderboardViewStyle = t11;
  let t12;
  bb3: {
    if (!navAdjustedSpaces.measurementsComplete) {
      let t13;
      if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
        t13 = {};
        $[23] = t13;
      } else {
        t13 = $[23];
      }
      t12 = t13;
      break bb3;
    }
    let t13;
    if ($[24] !== navAdjustedSpaces) {
      t13 = calculateHeightStyle(navAdjustedSpaces, 0);
      $[24] = navAdjustedSpaces;
      $[25] = t13;
    } else {
      t13 = $[25];
    }
    t12 = t13;
  }
  const winnersViewStyle = t12;
  let t13;
  bb4: {
    if (!navAdjustedSpaces.measurementsComplete) {
      let t14;
      if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = {};
        $[26] = t14;
      } else {
        t14 = $[26];
      }
      t13 = t14;
      break bb4;
    }
    let t14;
    if ($[27] !== navAdjustedSpaces) {
      t14 = calculateHeightStyle(navAdjustedSpaces, 0);
      $[27] = navAdjustedSpaces;
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    t13 = t14;
  }
  const salesViewStyle = t13;
  let t14;
  bb5: {
    if (!navAdjustedSpaces.measurementsComplete) {
      let t15;
      if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
        t15 = {};
        $[29] = t15;
      } else {
        t15 = $[29];
      }
      t14 = t15;
      break bb5;
    }
    let t15;
    if ($[30] !== navAdjustedSpaces) {
      t15 = calculateHeightStyle(navAdjustedSpaces, 0);
      $[30] = navAdjustedSpaces;
      $[31] = t15;
    } else {
      t15 = $[31];
    }
    t14 = t15;
  }
  const myVotesViewStyle = t14;
  let t15;
  bb6: {
    if (!navAdjustedSpaces.measurementsComplete) {
      let t16;
      if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = {};
        $[32] = t16;
      } else {
        t16 = $[32];
      }
      t15 = t16;
      break bb6;
    }
    let t16;
    if ($[33] !== navAdjustedSpaces) {
      t16 = calculateHeightStyle(navAdjustedSpaces, 0);
      $[33] = navAdjustedSpaces;
      $[34] = t16;
    } else {
      t16 = $[34];
    }
    t15 = t16;
  }
  const outcomeViewStyle = t15;
  let t16;
  bb7: {
    if (!navAdjustedSpaces.measurementsComplete) {
      let t17;
      if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
        t17 = {};
        $[35] = t17;
      } else {
        t17 = $[35];
      }
      t16 = t17;
      break bb7;
    }
    let t17;
    if ($[36] !== navAdjustedSpaces) {
      t17 = calculateHeightStyle(navAdjustedSpaces, 0);
      $[36] = navAdjustedSpaces;
      $[37] = t17;
    } else {
      t17 = $[37];
    }
    t16 = t17;
  }
  const faqViewStyle = t16;
  let t17;
  bb8: {
    if (!navAdjustedSpaces.measurementsComplete) {
      let t18;
      if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
        t18 = {};
        $[38] = t18;
      } else {
        t18 = $[38];
      }
      t17 = t18;
      break bb8;
    }
    let t18;
    if ($[39] !== navAdjustedSpaces) {
      t18 = calculateHeightStyle(navAdjustedSpaces, 0);
      $[39] = navAdjustedSpaces;
      $[40] = t18;
    } else {
      t18 = $[40];
    }
    t17 = t18;
  }
  const notificationsViewStyle = t17;
  let t18;
  bb9: {
    if (!navAdjustedSpaces.measurementsComplete) {
      let t19;
      if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
        t19 = {};
        $[41] = t19;
      } else {
        t19 = $[41];
      }
      t18 = t19;
      break bb9;
    }
    let t19;
    if ($[42] !== navAdjustedSpaces) {
      t19 = calculateHeightStyle(navAdjustedSpaces, 0);
      $[42] = navAdjustedSpaces;
      $[43] = t19;
    } else {
      t19 = $[43];
    }
    t18 = t19;
  }
  const myStreamFeedStyle = t18;
  let t19;
  bb10: {
    if (!navAdjustedSpaces.measurementsComplete) {
      let t20;
      if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
        t20 = {};
        $[44] = t20;
      } else {
        t20 = $[44];
      }
      t19 = t20;
      break bb10;
    }
    let t20;
    if ($[45] !== navAdjustedSpaces) {
      const homepageSpaces = {
        ...navAdjustedSpaces,
        headerSpace: 0,
        spacerSpace: 0
      };
      t20 = calculateHeightStyle(homepageSpaces, 0);
      $[45] = navAdjustedSpaces;
      $[46] = t20;
    } else {
      t20 = $[46];
    }
    t19 = t20;
  }
  const homepageFeedStyle = t19;
  let t20;
  bb11: {
    if (!navAdjustedSpaces.measurementsComplete) {
      let t21;
      if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
        t21 = {};
        $[47] = t21;
      } else {
        t21 = $[47];
      }
      t20 = t21;
      break bb11;
    }
    const totalOffset = navAdjustedSpaces.headerSpace + navAdjustedSpaces.pinnedSpace + navAdjustedSpaces.tabsSpace + navAdjustedSpaces.spacerSpace + navAdjustedSpaces.mobileTabsSpace + navAdjustedSpaces.mobileNavSpace;
    const heightCalc = `calc(100dvh - ${totalOffset}px)`;
    let t21;
    if ($[48] !== heightCalc) {
      t21 = {
        height: heightCalc,
        maxHeight: heightCalc
      };
      $[48] = heightCalc;
      $[49] = t21;
    } else {
      t21 = $[49];
    }
    t20 = t21;
  }
  const smallScreenFeedStyle = t20;
  let t21;
  bb12: {
    if (!navAdjustedSpaces.measurementsComplete) {
      let t22;
      if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
        t22 = {};
        $[50] = t22;
      } else {
        t22 = $[50];
      }
      t21 = t22;
      break bb12;
    }
    let t22;
    if ($[51] !== navAdjustedSpaces) {
      t22 = calculateHeightStyle(navAdjustedSpaces, 0);
      $[51] = navAdjustedSpaces;
      $[52] = t22;
    } else {
      t22 = $[52];
    }
    t21 = t22;
  }
  const mobileWavesViewStyle = t21;
  let t22;
  bb13: {
    if (!navAdjustedSpaces.measurementsComplete) {
      let t23;
      if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
        t23 = {};
        $[53] = t23;
      } else {
        t23 = $[53];
      }
      t22 = t23;
      break bb13;
    }
    let t23;
    if ($[54] !== navAdjustedSpaces) {
      t23 = calculateHeightStyle(navAdjustedSpaces, 0);
      $[54] = navAdjustedSpaces;
      $[55] = t23;
    } else {
      t23 = $[55];
    }
    t22 = t23;
  }
  const mobileAboutViewStyle = t22;
  let t23;
  if ($[56] !== contentContainerStyle || $[57] !== faqViewStyle || $[58] !== homepageFeedStyle || $[59] !== leaderboardViewStyle || $[60] !== mobileAboutViewStyle || $[61] !== mobileWavesViewStyle || $[62] !== myStreamFeedStyle || $[63] !== myVotesViewStyle || $[64] !== notificationsViewStyle || $[65] !== outcomeViewStyle || $[66] !== salesViewStyle || $[67] !== smallScreenFeedStyle || $[68] !== spaces || $[69] !== waveViewStyle || $[70] !== winnersViewStyle) {
    t23 = {
      spaces,
      registerRef,
      contentContainerStyle,
      waveViewStyle,
      leaderboardViewStyle,
      winnersViewStyle,
      salesViewStyle,
      myVotesViewStyle,
      outcomeViewStyle,
      faqViewStyle,
      notificationsViewStyle,
      myStreamFeedStyle,
      homepageFeedStyle,
      smallScreenFeedStyle,
      mobileWavesViewStyle,
      mobileAboutViewStyle
    };
    $[56] = contentContainerStyle;
    $[57] = faqViewStyle;
    $[58] = homepageFeedStyle;
    $[59] = leaderboardViewStyle;
    $[60] = mobileAboutViewStyle;
    $[61] = mobileWavesViewStyle;
    $[62] = myStreamFeedStyle;
    $[63] = myVotesViewStyle;
    $[64] = notificationsViewStyle;
    $[65] = outcomeViewStyle;
    $[66] = salesViewStyle;
    $[67] = smallScreenFeedStyle;
    $[68] = spaces;
    $[69] = waveViewStyle;
    $[70] = winnersViewStyle;
    $[71] = t23;
  } else {
    t23 = $[71];
  }
  const contextValue = t23;
  let t24;
  if ($[72] !== children || $[73] !== contextValue) {
    t24 = <LayoutContext.Provider value={contextValue}>{children}</LayoutContext.Provider>;
    $[72] = children;
    $[73] = contextValue;
    $[74] = t24;
  } else {
    t24 = $[74];
  }
  return t24;
};

// Custom hook to use the layout context
export const useLayout = () => {
  return useContext(LayoutContext);
};
function _temp() {}